"use client"

import React, { useState } from "react"
import { Button } from "@/components/ui/button"
import { ChevronDown, ChevronRight, Copy, Plus } from "lucide-react"
import { toast } from "sonner"
import { copyToClipboard } from "@/lib/clipboard"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface JsonTreeProps {
  data: unknown
  /** Invoked with the dot/array-notation path (AtsFieldMapping.SourceField format) */
  onAddToMapping?: (path: string) => void
}

interface JsonNodeProps extends JsonTreeProps {
  nodeKey: string | null
  path: string
  depth: number
}

/**
 * Collapsible JSON viewer. Paths use the same notation accepted by
 * AtsFieldMapping.SourceField: nested objects as "parent.child", arrays as
 * "items[].child" — so a clicked field can be used directly as a source field.
 */
export function JsonTree({ data, onAddToMapping }: JsonTreeProps) {
  return (
    <div className="font-mono text-xs leading-6">
      <JsonNode data={data} nodeKey={null} path="" depth={0} onAddToMapping={onAddToMapping} />
    </div>
  )
}

function appendPath(path: string, key: string): string {
  return path ? `${path}.${key}` : key
}

function JsonNode({ data, nodeKey, path, depth, onAddToMapping }: JsonNodeProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(depth < 2)

  const copyPath = async () => {
    if (await copyToClipboard(path)) {
      toast.success(t("explorer.common.copiedPath", { path }))
    } else {
      toast.error(t("explorer.jsonTree.copyFailed"), { description: path })
    }
  }

  const rowActions = path ? (
    <span className="invisible group-hover:visible inline-flex gap-0.5 ml-2 align-middle">
      <Button variant="ghost" size="icon" className="h-5 w-5" title={t("explorer.jsonTree.copyPathTitle", { path })} onClick={copyPath}>
        <Copy className="h-3 w-3" />
      </Button>
      {onAddToMapping && (
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          title={t("explorer.jsonTree.addSourceField", { path })}
          onClick={() => onAddToMapping(path)}
        >
          <Plus className="h-3 w-3" />
        </Button>
      )}
    </span>
  ) : null

  const keyLabel = nodeKey !== null ? <span className="text-blue-700">{nodeKey}: </span> : null

  if (Array.isArray(data)) {
    return (
      <div className="group">
        <button className="inline-flex items-center" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {keyLabel}
          <span className="text-muted-foreground">[{data.length}]</span>
        </button>
        {rowActions}
        {expanded && (
          <div className="pl-4 border-l border-border ml-1.5">
            {data.map((item, index) => (
              <JsonNode
                key={index}
                data={item}
                nodeKey={String(index)}
                path={`${path}[]`}
                depth={depth + 1}
                onAddToMapping={onAddToMapping}
              />
            ))}
            {data.length === 0 && <span className="text-muted-foreground italic">{t("explorer.common.empty")}</span>}
          </div>
        )}
      </div>
    )
  }

  if (data !== null && typeof data === "object") {
    const entries = Object.entries(data as Record<string, unknown>)
    return (
      <div className="group">
        <button className="inline-flex items-center" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          {keyLabel}
          <span className="text-muted-foreground">{"{"}{entries.length}{"}"}</span>
        </button>
        {rowActions}
        {expanded && (
          <div className="pl-4 border-l border-border ml-1.5">
            {entries.map(([key, value]) => (
              <JsonNode
                key={key}
                data={value}
                nodeKey={key}
                path={appendPath(path, key)}
                depth={depth + 1}
                onAddToMapping={onAddToMapping}
              />
            ))}
            {entries.length === 0 && <span className="text-muted-foreground italic">{t("explorer.common.empty")}</span>}
          </div>
        )}
      </div>
    )
  }

  // Leaf value
  return (
    <div className="group pl-4">
      {keyLabel}
      <LeafValue value={data} />
      {rowActions}
    </div>
  )
}

function LeafValue({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground italic">null</span>
  }
  if (typeof value === "string") {
    return <span className="text-green-700">&quot;{value}&quot;</span>
  }
  if (typeof value === "boolean") {
    return <span className="text-purple-700">{String(value)}</span>
  }
  return <span className="text-orange-700">{String(value)}</span>
}
